import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Descriptions,
  Table,
  Button,
  Space,
  Tag,
  Divider,
  Typography,
  Modal,
  Form,
  Input,
  InputNumber,
  notification,
  Row,
  Col,
  Statistic,
  Alert,
  Grid
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ShoppingCartOutlined,
  PrinterOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { purchaseService } from "../../services/purchaseService";
import PurchaseForm from "./PurchaseForm";

const { Title, Text } = Typography;
const { Column } = Table;
const { useBreakpoint } = Grid;

const PurchaseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [purchase, setPurchase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isProcessModalVisible, setIsProcessModalVisible] = useState(false);
  const [processForm] = Form.useForm();

  const isMobile = !screens.md;

  useEffect(() => {
    fetchPurchaseDetails();
  }, [id]);

  const fetchPurchaseDetails = async () => {
    try {
      setLoading(true);
      const response = await purchaseService.getPurchaseById(id);
      if (response.success) {
        setPurchase(response.data);

        // Initialize process form with item groups
        if (response.data.items) {
          const itemGroups = response.data.items.map((item) => ({
            itemId: item.itemId,
            itemName: item.item.itemName,
            itemType: item.item.itemType,
            quantity: item.quantity,
            serialNumbersText: "",
            batchId: "",
            brand: "",
          }));
          processForm.setFieldsValue({ itemGroups });
        }
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          error.response?.data?.error ||
          "Failed to fetch purchase invoice details",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProcessPurchase = async (values) => {
    try {
      setProcessing(true);

      const { itemGroups } = values;
      const assets = [];
      const errors = [];

      itemGroups.forEach((group) => {
        const sns = group.serialNumbersText
          ? group.serialNumbersText.split(",").map(s => s.trim()).filter(s => s.length > 0)
          : [];

        if (sns.length !== group.quantity) {
          errors.push(`${group.itemName}: Required ${group.quantity} serial numbers, but found ${sns.length}`);
        }

        sns.forEach(sn => {
          assets.push({
            itemId: group.itemId,
            serialNumber: sn,
            batchId: group.batchId,
            brand: group.brand
          });
        });
      });

      if (errors.length > 0) {
        notification.error({
          message: "Validation Error",
          description: (
            <ul style={{ paddingLeft: 16 }}>
              {errors.map((err, i) => <li key={i}>{err}</li>)}
            </ul>
          )
        });
        return;
      }

      const response = await purchaseService.processPurchase(id, { assets });
      notification.success({
        message: "Success",
        description: response.message || "Assets created successfully",
      });
      setIsProcessModalVisible(false);
      processForm.resetFields();
      fetchPurchaseDetails();
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          error.response?.data?.error || "Failed to process purchase invoice",
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!purchase) {
    return (
      <Card>
        <Alert
          message="Purchase Invoice Not Found"
          description="The purchase invoice you're looking for doesn't exist."
          type="error"
          showIcon
          action={
            <Button onClick={() => navigate("/purchases")}>
              Back to Purchases
            </Button>
          }
        />
      </Card>
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: "100%", padding: isMobile ? '8px' : '0' }}>
      <Card bodyStyle={{ padding: isMobile ? '12px' : '24px' }}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Row justify="space-between" align="middle" gutter={[16, 16]}>
            <Col xs={24} md={16}>
              <Space align="start">
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate("/purchases")}
                />
                <div>
                  <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>
                    Purchase Invoice #{purchase.invoiceNumber}
                  </Title>
                  <Text type="secondary">
                    {new Date(purchase.invoiceDate).toLocaleDateString()}
                  </Text>
                </div>
              </Space>
            </Col>
            <Col xs={24} md={8} style={{ textAlign: isMobile ? 'left' : 'right' }}>
              <Space wrap>
                <Button
                  icon={<EditOutlined />}
                  onClick={() => setIsEditModalVisible(true)}
                  block={isMobile}
                >
                  Edit
                </Button>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => setIsProcessModalVisible(true)}
                  block={isMobile}
                >
                  Process to Inventory
                </Button>
              </Space>
            </Col>
          </Row>
        </Space>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Invoice Details" bodyStyle={{ padding: isMobile ? '12px' : '24px' }}>
            <Descriptions column={{ xs: 1, sm: 2 }} bordered size={isMobile ? "small" : "default"}>
              <Descriptions.Item label="Invoice Number">
                <strong>{purchase.invoiceNumber}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Invoice Date">
                {new Date(purchase.invoiceDate).toLocaleDateString()}
              </Descriptions.Item>
              <Descriptions.Item label="Vendor" span={2}>
                {purchase.vendor?.vendorName}
              </Descriptions.Item>
              {purchase.vendor?.gstn && (
                <Descriptions.Item label="Vendor GSTN">
                   {purchase.vendor.gstn}
                </Descriptions.Item>
              )}
              {purchase.vendor?.vendorAddress && (
                <Descriptions.Item label="Vendor Address" span={2}>
                   {purchase.vendor.vendorAddress}
                </Descriptions.Item>
              )}
              {purchase.ewayBillNo && (
                <Descriptions.Item label="E-Way Bill No">
                  {purchase.ewayBillNo}
                </Descriptions.Item>
              )}
              {purchase.cellGstInvoice && (
                <Descriptions.Item label="Cell GST Invoice">
                  {purchase.cellGstInvoice}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider>Items</Divider>
            <Table
              dataSource={purchase.items || []}
              rowKey="id"
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
            >
              <Column
                title="Item Name"
                dataIndex={["item", "itemName"]}
                key="itemName"
              />
              <Column
                title="Type"
                dataIndex={["item", "itemType"]}
                key="itemType"
              />
              <Column
                title="Quantity"
                dataIndex="quantity"
                key="quantity"
                align="right"
              />
              <Column
                title="Rate"
                dataIndex="rate"
                key="rate"
                align="right"
                render={(rate) => `₹${parseFloat(rate || 0).toFixed(2)}`}
              />
              <Column
                title="Amount Before Tax"
                dataIndex="amountBeforeTax"
                key="amountBeforeTax"
                align="right"
                render={(amount) => `₹${parseFloat(amount || 0).toFixed(2)}`}
              />
              <Column
                title="Tax"
                dataIndex="taxAmount"
                key="taxAmount"
                align="right"
                render={(amount) => `₹${parseFloat(amount || 0).toFixed(2)}`}
              />
              <Column
                title="Total Amount"
                dataIndex="totalAmount"
                key="totalAmount"
                align="right"
                render={(amount) => (
                  <strong>₹{parseFloat(amount || 0).toFixed(2)}</strong>
                )}
              />
            </Table>

            {purchase.amountInWords && (
              <>
                <Divider>Amount in Words</Divider>
                <Text>{purchase.amountInWords}</Text>
              </>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Summary">
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <Statistic
                title="Total Before Tax"
                value={purchase.totalBeforeTax}
                prefix="₹"
                precision={2}
              />
              <Statistic
                title="Total Tax"
                value={purchase.totalTax}
                prefix="₹"
                precision={2}
              />
              <Divider />
              <Statistic
                title="Total After Tax"
                value={purchase.totalAfterTax}
                prefix="₹"
                precision={2}
                valueStyle={{ color: "#52c41a", fontSize: isMobile ? 20 : 24 }}
              />
            </Space>
          </Card>

          {purchase.taxDetails && purchase.taxDetails.length > 0 && (
            <Card title="Tax Details" style={{ marginTop: 16 }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="CGST">
                  ₹
                  {parseFloat(purchase.taxDetails[0].cgstAmount || 0).toFixed(
                    2
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="SGST">
                  ₹
                  {parseFloat(purchase.taxDetails[0].sgstAmount || 0).toFixed(
                    2
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="IGST">
                  ₹
                  {parseFloat(purchase.taxDetails[0].igstAmount || 0).toFixed(
                    2
                  )}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}
        </Col>
      </Row>

      <Modal
        title="Edit Purchase Invoice"
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={null}
        width={isMobile ? '100%' : 800}
        style={isMobile ? { top: 0, margin: 0, maxWidth: '100vw' } : {}}
        bodyStyle={isMobile ? { padding: '12px' } : {}}
        destroyOnClose
      >
        <PurchaseForm
          purchase={purchase}
          onSuccess={() => {
            setIsEditModalVisible(false);
            fetchPurchaseDetails();
          }}
          onCancel={() => setIsEditModalVisible(false)}
        />
      </Modal>

      <Modal
        title="Process Purchase to Inventory"
        open={isProcessModalVisible}
        onCancel={() => setIsProcessModalVisible(false)}
        onOk={() => processForm.submit()}
        confirmLoading={processing}
        width={isMobile ? '100%' : 900}
        style={isMobile ? { top: 0, margin: 0, maxWidth: '100vw' } : {}}
        okText="Create Assets"
      >
        <Alert
          message="Create Serialized Assets"
          description="Enter serial numbers and details for each item."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form
          form={processForm}
          layout="vertical"
          onFinish={handleProcessPurchase}
        >
          <Form.List name="itemGroups">
            {(fields) => (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {fields.map((field) => {
                  const group = processForm.getFieldValue(["itemGroups", field.name]);
                  return (
                    <Card
                      key={field.key}
                      size="small"
                      title={`${group?.itemName} (${group?.itemType})`}
                      extra={<Tag color="blue">Qty: {group?.quantity}</Tag>}
                      style={{ border: '1px solid #f0f0f0' }}
                    >
                      <Form.Item name={[field.name, "itemId"]} hidden>
                        <Input />
                      </Form.Item>
                      <Form.Item name={[field.name, "itemName"]} hidden>
                        <Input />
                      </Form.Item>
                      <Form.Item name={[field.name, "itemType"]} hidden>
                        <Input />
                      </Form.Item>
                      <Form.Item name={[field.name, "quantity"]} hidden>
                        <Input />
                      </Form.Item>

                      <Row gutter={16}>
                        <Col span={24}>
                          <Form.Item
                            label={
                              <Space wrap>
                                <span>Serial Numbers ({group?.quantity} required)</span>
                                <Text type="secondary" style={{ fontSize: '11px' }}>
                                  Separate each with a comma
                                </Text>
                              </Space>
                            }
                            name={[field.name, "serialNumbersText"]}
                            rules={[{ required: true, message: "Required" }]}
                          >
                            <Input.TextArea
                              placeholder={`Enter ${group?.quantity} serial numbers separated by commas`}
                              autoSize={{ minRows: 2, maxRows: 6 }}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label="Batch ID (Optional)"
                            name={[field.name, "batchId"]}
                          >
                            <Input placeholder="Batch ID" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            label="Brand (Optional)"
                            name={[field.name, "brand"]}
                          >
                            <Input placeholder="Brand" />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  );
                })}
              </div>
            )}
          </Form.List>
        </Form>
      </Modal>
    </Space>
  );
};

export default PurchaseDetail;
